// src/components/Sidebar.js
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Star, Settings,
  LogOut, MessageSquare, Zap, Sun, Moon, Shield, CreditCard, Receipt,
  Globe, Mail, Lock, ChevronsLeft, ChevronsRight, ChevronDown, Sparkles, LifeBuoy, Wrench, MapPin,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getBusinessType } from "../lib/businessType";
import { useTheme } from "../context/ThemeContext";

// Top-level items every business sees.
const topNav = [
  { to: "/overview", icon: LayoutDashboard, label: "Overview" },
];

// Website-related services — flat links, siblings of the Zilo AI item.
// packageKey can be a list — any one of them unlocks the item. `special`
// items are plans the team offers directly (Google Growth, Website
// Management): they only appear for clients who have them. The rest show
// with a lock until bought.
const servicesNav = [
  { to: "/web-development",    icon: Globe,  label: "Web Development",    packageKey: "webDevelopment" },
  { to: "/website-management", icon: Wrench, label: "Website Management", packageKey: "websiteManagement", special: true },
  { to: "/google-growth",      icon: MapPin, label: "Google Growth",      packageKey: ["googleEssentials", "googlePro"], special: true },
  { to: "/email-marketing",    icon: Mail,   label: "Email Marketing",    packageKey: "emailMarketing" },
];

const accountNav = [
  { to: "/settings",  icon: Settings,        label: "Settings"  },
  { to: "/billing",   icon: Receipt,    label: "Billing"  },
  { to: "/pricing",   icon: CreditCard, label: "Plans"  },
  { to: "/support",   icon: LifeBuoy,   label: "Support"  },
];

export default function Sidebar({ open = false, onClose = () => {}, collapsed = false, onToggleCollapse = () => {} }) {
  const { logout, businessProfile } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const isAdmin = businessProfile?.role === "admin";
  const businessType = getBusinessType(businessProfile);
  const showZilo = businessType === "zilo" || businessType === "both";
  const aiAgentName = businessProfile?.aiAgentName || "Zilo";

  const [ziloOpen, setZiloOpen] = useState(true);

  // Zilo's own dashboard is named after the agent itself — "Overview"
  // would just repeat the top-level page's name.
  const ziloNav = [
    { to: "/dashboard", icon: Sparkles,      label: `${aiAgentName} Dashboard` },
    { to: "/clients",   icon: Users,         label: "Clients" },
    { to: "/reviews",   icon: Star,          label: "Reviews" },
    { to: "/campaigns", icon: MessageSquare, label: "Campaigns" },
  ];

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  function renderNavItem({ to, icon: Icon, label, packageKey }) {
    const locked = packageKey && ![].concat(packageKey).some(k => businessProfile?.packages?.[k]);
    return (
      <NavLink
        key={to} to={to}
        onClick={onClose}
        title={collapsed ? label : undefined}
        style={({ isActive }) => ({
          display: "flex", alignItems: "center", gap: 10,
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? "10px 0" : "10px 12px", borderRadius: "var(--radius)",
          fontSize: 14, fontWeight: 500,
          color: isActive ? "var(--nav-active-text)" : "var(--nav-text)",
          background: isActive ? "var(--nav-active-bg)" : "transparent",
          border: isActive ? "1px solid var(--nav-active-border)" : "1px solid transparent",
          marginBottom: 2, transition: "all .15s", textDecoration: "none",
        })}
      >
        {({ isActive }) => (
          <>
            <Icon size={16} style={{ flexShrink: 0 }}/>
            {!collapsed && (
              <>
                {label}
                {locked ? (
                  <Lock size={12} style={{ marginLeft: "auto", opacity: .5 }} />
                ) : isActive && (
                  <div style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: "var(--nav-active-text)", boxShadow: "0 0 8px var(--accent-glow)" }}/>
                )}
              </>
            )}
          </>
        )}
      </NavLink>
    );
  }

  function renderZiloItem() {
    return (
      <button
        onClick={() => setZiloOpen(o => !o)}
        title={collapsed ? `${aiAgentName} AI` : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%",
          justifyContent: collapsed ? "center" : "flex-start",
          padding: collapsed ? "10px 0" : "10px 12px", borderRadius: "var(--radius)",
          fontSize: 14, fontWeight: 500, color: "var(--nav-text)",
          background: "transparent", border: "1px solid transparent", cursor: "pointer",
          marginBottom: 2, transition: "all .15s",
        }}
      >
        <Sparkles size={16} style={{ flexShrink: 0 }} />
        {!collapsed && (
          <>
            {aiAgentName} AI
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--green)", boxShadow: "0 0 6px var(--green)", animation: "pulse 2s ease infinite" }} />
            <ChevronDown size={12} color="rgba(255,255,255,.35)" style={{ marginLeft: "auto", transform: ziloOpen ? "none" : "rotate(-90deg)", transition: "transform .15s" }} />
          </>
        )}
      </button>
    );
  }

  return (
    <aside
      className={`rb-sidebar${open ? " rb-sidebar-open" : ""}`}
      style={{
      width: collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)",
      height: "100vh",
      background: "linear-gradient(180deg, var(--sidebar-bg) 0%, var(--sidebar-bg-2) 100%)",
      borderRight: "1px solid var(--sidebar-border)",
      display: "flex",
      flexDirection: "column",
      position: "fixed",
      left: 0, top: 0,
      zIndex: 100,
      transition: "width .2s ease",
      overflow: "visible",
    }}>
      {/* Collapse toggle — floating circular button straddling the sidebar's right edge */}
      <button
        onClick={onToggleCollapse}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        style={{
          position: "absolute", top: 34, right: -13, zIndex: 101,
          width: 26, height: 26, borderRadius: "50%",
          background: "var(--accent)", border: "3px solid var(--surface-2)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", cursor: "pointer", transition: "background .15s ease",
          padding: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "var(--accent-dim)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "var(--accent)"; }}
      >
        {collapsed ? <ChevronsRight size={13}/> : <ChevronsLeft size={13}/>}
      </button>
      {/* Logo */}
      <div style={{ padding: collapsed ? "22px 14px 18px" : "22px 20px 18px", borderBottom: "1px solid var(--sidebar-border)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: collapsed ? "center" : "flex-start" }}>
          <img
            src={collapsed ? "https://techbugz.com/wp-content/uploads/2026/04/tech-bugz-icon-new.png" : "/logo.png"}
            alt="Tech Bugz"
            style={{
              height: collapsed ? 32 : 45,
              width: collapsed ? 32 : "auto",
              maxWidth: 160,
              objectFit: "contain",
              flexShrink: 0,
              borderRadius: collapsed ? 8 : 0,
            }}
          />

          {/* Theme toggle */}
          {!collapsed && (
            <button
              onClick={toggleTheme}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              style={{
                marginLeft: "auto", width: 32, height: 32,
                background: "var(--surface-4)", border: "1px solid var(--border-2)",
                borderRadius: "var(--radius)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "rgba(255,255,255,.5)", cursor: "pointer", flexShrink: 0, transition: "all .2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--accent-lt)"; e.currentTarget.style.color = "var(--accent)"; e.currentTarget.style.borderColor = "var(--accent-border)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--surface-4)"; e.currentTarget.style.color = "rgba(255,255,255,.5)"; e.currentTarget.style.borderColor = "var(--border-2)"; }}
            >
              {isDark ? <Sun size={14}/> : <Moon size={14}/>}
            </button>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: collapsed ? "6px 8px" : "6px 12px", overflowY: "auto", overflowX: "hidden" }}>
        {topNav.map(renderNavItem)}

        {!collapsed && (
          <p style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".07em", margin: "14px 12px 6px" }}>
            Services
          </p>
        )}
        {collapsed && <div style={{ height: 1, background: "var(--sidebar-border)", margin: "10px 4px" }} />}

        {showZilo && (
          <>
            {renderZiloItem()}
            {(ziloOpen || collapsed) && (
              <div style={{ marginLeft: collapsed ? 0 : 8, paddingLeft: collapsed ? 0 : 8, borderLeft: collapsed ? "none" : "1px solid var(--sidebar-border)" }}>
                {ziloNav.map(renderNavItem)}
              </div>
            )}
          </>
        )}
        {servicesNav
          .filter(({ packageKey, to, special }) => {
            const has = k => !!businessProfile?.packages?.[k];
            // Special plans only for clients who have them; others show locked.
            if (special && ![].concat(packageKey).some(has)) return false;
            // A site we're building/hosting already covers upkeep — one item, not two.
            if (to === "/website-management" && has("webDevelopment")) return false;
            return true;
          })
          .map(renderNavItem)}

        {!collapsed && (
          <p style={{ fontSize: 10.5, fontWeight: 700, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: ".07em", margin: "14px 12px 6px" }}>
            Account
          </p>
        )}
        {collapsed && <div style={{ height: 1, background: "var(--sidebar-border)", margin: "10px 4px" }} />}
        {accountNav.map(renderNavItem)}
      </nav>

      {/* Business pill */}
      {businessProfile && (
        <div style={{ padding: collapsed ? "10px 8px" : "10px 14px" }}>
          <div style={{
            background: "var(--surface-4)", border: "1px solid var(--border)",
            borderRadius: "var(--radius)", padding: collapsed ? "8px" : "8px 12px",
            display: "flex", alignItems: "center", gap: 8,
            justifyContent: collapsed ? "center" : "flex-start",
          }} title={collapsed ? businessProfile.businessName : undefined}>
            {businessProfile.logoUrl ? (
              <img src={businessProfile.logoUrl} alt="logo" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}/>
            ) : (
              <div style={{ width: 26, height: 26, background: "linear-gradient(135deg, var(--accent), var(--accent-dim))", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                {businessProfile.businessName?.[0]?.toUpperCase() || "B"}
              </div>
            )}
            {!collapsed && (
              <span style={{ fontSize: 12, color: "rgba(255,255,255,.55)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {businessProfile.businessName}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Bottom */}
      <div style={{ padding: collapsed ? "10px 8px" : "10px 12px", borderTop: "1px solid var(--sidebar-border)" }}>
        {/* AI pill */}
        {showZilo && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "8px" : "8px 12px", marginBottom: 6,
            background: "var(--green-lt)", border: "1px solid rgba(16,185,129,.2)", borderRadius: "var(--radius)",
          }} title={collapsed ? `${aiAgentName} Active` : undefined}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--green)", animation: "pulse 2s ease infinite", flexShrink: 0 }}/>
            {!collapsed && <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 600 }}>{aiAgentName} Active</span>}
            {!collapsed && <Zap size={11} color="var(--green)" style={{ marginLeft: "auto" }}/>}
          </div>
        )}

        {/* Admin link — only for admins */}
        {isAdmin && (
          <a
            href="/admin"
            title={collapsed ? "Admin Panel" : undefined}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              justifyContent: collapsed ? "center" : "flex-start",
              padding: collapsed ? "9px 0" : "9px 12px", marginBottom: 6,
              background: "var(--accent-lt)", border: "1px solid var(--accent-border)",
              borderRadius: "var(--radius)", color: "var(--accent)",
              fontSize: 13, fontWeight: 600, textDecoration: "none", transition: "all .15s",
            }}
          >
            <Shield size={15}/> {!collapsed && "Admin Panel"}
          </a>
        )}

        {/* Sign out */}
        <button
          onClick={handleLogout}
          title={collapsed ? "Sign out" : undefined}
          style={{
            display: "flex", alignItems: "center", gap: 10, width: "100%",
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "9px 0" : "9px 12px",
            background: "none", border: "1px solid transparent", borderRadius: "var(--radius)",
            color: "rgba(255,255,255,.35)", fontSize: 13, fontWeight: 500, cursor: "pointer", transition: "all .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "var(--red-lt)"; e.currentTarget.style.color = "var(--red)"; e.currentTarget.style.borderColor = "rgba(239,68,68,.2)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "rgba(255,255,255,.35)"; e.currentTarget.style.borderColor = "transparent"; }}
        >
          <LogOut size={15}/> {!collapsed && "Sign out"}
        </button>

        {!collapsed && (
          <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, color: "rgba(255,255,255,.3)" }}>
            v1.5.0 · © 2026 TechBugz
          </div>
        )}
      </div>
    </aside>
  );
}
